import jwtDecode from 'jwt-decode';
import { Auth } from 'aws-amplify';

import { SET_USER, SET_CREDENTIALS } from '@/assets/src/store/mutation_types';
import Store from '@/store';

const secondsBeforeExpiryToRefresh = 5;

/**
 * Sets user object in the store.
 * @param {Object} user - The authenticated user object.
 */
function setUserInStore(user: any): void {
  Store.commit(SET_USER, user);
}

/**
 * Sets credentials object in the store.
 * @param {Object} credentials - The authenticated credentials object.
 */
function setCredentialsInStore(credentials: any): void {
  Store.commit(SET_CREDENTIALS, credentials);
}

/**
 * Checks if a JWT is close to expire.
 * @param {string} encodedJwt - The JWT to check for expiration.
 * @returns {boolean} Whether the JWT will expire within
 *     {@link secondsBeforeExpiryToRefresh} seconds.
 */
function isExpired(encodedJwt: any): boolean {
  const decodedJwt = jwtDecode(encodedJwt);
  // @ts-ignore
  return decodedJwt.exp < new Date() / 1000 + secondsBeforeExpiryToRefresh;
}

/**
 * Refreshes the user by fetching the current authenticated user
 * and updates the user in the store.
 */
function refreshUser(): Promise<void> {
  return Auth.currentAuthenticatedUser().then(setUserInStore);
}

/**
 * Gets the JWT of the ID token on the authenticated user.
 * @returns {string} The JWT of the ID token on the authenticated user.
 *     Returns null if the user is not authenticated.
 */
async function getUserIdTokenJwt(): Promise<string | null> {
  if (!Store.state.user) {
    // Logger.error("Expected to find a user in the store.", getUserIdTokenJwt.name);
    return null;
  }

  if (isExpired(Store.state.user.signInUserSession.idToken.jwtToken)) {
    await refreshUser();
  }

  return Store.state.user.signInUserSession.idToken.jwtToken;
}

/**
 * Gets the JWT of the Access token on the authenticated user.
 * @returns {string} The JWT of the Access token on the authenticated user.
 *     Returns null if the user is not authenticated.
 */
async function getAccessTokenJwt() {
  if (!Store.state.user) {
    // Logger.error("Expected to find a user in the store.", getAccessTokenJwt.name);
    return null;
  }

  if (isExpired(Store.state.user.signInUserSession.accessToken.jwtToken)) {
    await refreshUser();
  }

  return Store.state.user.signInUserSession.accessToken.jwtToken;
}

/**
 * Gets the Authorization header with Access Token JWT as the value.
 * @returns {Object} An object containing the Access Token JWT as the Authorization header.
 */
async function getAccessTokenJwtHeader() {
  const accessTokenJwt = await getAccessTokenJwt();
  return { Authorization: accessTokenJwt };
}

/**
 * Gets the Authorization header with ID Token JWT as the value.
 * @returns {Object} An object containing the ID Token JWT as the Authorization header.
 */
async function getIdTokenJwtHeader() {
  const idTokenJwt = await getUserIdTokenJwt();
  return { Authorization: idTokenJwt };
}

export {
  setCredentialsInStore,
  setUserInStore,
  getUserIdTokenJwt,
  getAccessTokenJwt,
  getAccessTokenJwtHeader,
  getIdTokenJwtHeader,
};
